/**
 * 地图航线指标：data_up 行为 { one, two, three, four? }
 * one = 起点「纬度,经度」  two = 落点「纬度,经度」  three = 是否连线(0/1)  four = 落点展示名（可选）
 *
 * 兼容旧表头行：one/x =「起点经纬度」、two/y =「落点经纬度」、three/label =「是否连线」
 */

import {
  getIndicatorFieldByIndex,
} from '@/utils/mapIndicatorFields'

/** 航线连线与动效默认颜色（侧栏可配置 mapRouteLineColor，未配置时用此值） */
export const MAP_ROUTE_LINE_COLOR = '#5ED5FE'

/** 数据为 "纬度,经度"，转为 ECharts geo 用的 [经度, 纬度] */
export function parseLatLngStringToGeoCoord (str) {
  if (str == null || str === '') return null
  const parts = String(str).trim().split(',').map(p => parseFloat(p.trim()))
  if (parts.length !== 2 || parts.some(n => Number.isNaN(n))) return null
  const lat = parts[0]
  const lng = parts[1]
  return [lng, lat]
}

export function looksLikeLatLngPair (str) {
  return parseLatLngStringToGeoCoord(str) != null
}

function isRouteHeaderRow (row) {
  if (!row || typeof row !== 'object') return false
  const a = getIndicatorFieldByIndex(row, 1)
  const b = getIndicatorFieldByIndex(row, 2)
  const c = getIndicatorFieldByIndex(row, 3)
  return String(a) === '起点经纬度' &&
    String(b) === '落点经纬度' &&
    String(c) === '是否连线'
}

/** 单行是否为航线数据（one/two 均为经纬度；three 为 0/1 连线开关或为空） */
export function isLatLngRouteDataRow (row) {
  if (!row || typeof row !== 'object') return false
  const fromStr = getIndicatorFieldByIndex(row, 1)
  const toStr = getIndicatorFieldByIndex(row, 2)
  if (!looksLikeLatLngPair(fromStr) || !looksLikeLatLngPair(toStr)) return false
  const link = getIndicatorFieldByIndex(row, 3)
  if (link === undefined || link === null || link === '') return true
  if (link === 0 || link === '0' || link === 1 || link === '1' || link === true || link === false) {
    return true
  }
  return false
}

/**
 * 是否为航线指标 dataList（含旧表头，或首行起即为 one/two 经纬度对）
 */
export function isLatLngRouteIndicatorDataList (dataList) {
  if (!Array.isArray(dataList) || !dataList.length) return false
  if (isRouteHeaderRow(dataList[0])) return dataList.length >= 2
  return isLatLngRouteDataRow(dataList[0])
}

function routeDataRows (dataList) {
  if (!Array.isArray(dataList) || !dataList.length) return []
  if (isRouteHeaderRow(dataList[0])) return dataList.slice(1)
  return dataList
}

export function isLinkEnabled (label) {
  return label === 1 || label === '1' || label === true
}

/** 落点 four 文案 → tooltip HTML（支持字面量 /n、\\n 及真实换行） */
export function formatMapRoutePointLabelForTooltip (str) {
  if (str == null || String(str).trim() === '') return ''
  const escaped = String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return escaped
    .replace(/\/n/g, '<br/>')
    .replace(/\\n/g, '<br/>')
    .replace(/\r\n/g, '<br/>')
    .replace(/\n/g, '<br/>')
    .replace(/\r/g, '<br/>')
}

/**
 * @param {Array} dataList data_up 航线列表（可无表头）
 * @returns {{ scatter: Array, lines: Array }}
 */
export function buildMapRouteOverlayFromDataList (dataList) {
  const scatter = []
  const lines = []
  const rows = routeDataRows(dataList)
  rows.forEach((row) => {
    if (!isLatLngRouteDataRow(row)) return
    const from = parseLatLngStringToGeoCoord(getIndicatorFieldByIndex(row, 1))
    const to = parseLatLngStringToGeoCoord(getIndicatorFieldByIndex(row, 2))
    if (!to) return
    const rawFour = getIndicatorFieldByIndex(row, 4)
    const pointLabel = rawFour != null && String(rawFour).trim() !== ''
      ? String(rawFour).trim()
      : ''
    scatter.push({
      ...(pointLabel ? { name: pointLabel, pointLabel } : {}),
      value: to,
    })
    if (isLinkEnabled(getIndicatorFieldByIndex(row, 3)) && from) {
      lines.push({
        coords: [from, to],
        lineStyle: { color: MAP_ROUTE_LINE_COLOR },
      })
    }
  })
  return { scatter, lines }
}

export function mergeMapRouteOverlayParts (parts) {
  const scatter = []
  const lines = []
  parts.forEach(p => {
    if (p.scatter?.length) scatter.push(...p.scatter)
    if (p.lines?.length) lines.push(...p.lines)
  })
  if (!scatter.length && !lines.length) return null
  return { scatter, lines }
}

/**
 * @deprecated 地图已不再转换为 x/y；保留别名避免旧引用报错
 */
export function normalizeMapIndicatorDataListFromUp (dataList) {
  return dataList
}
